import React, { useState, useEffect } from 'react';
import TopNav from '../components/TopNav';
import SideIcons from '../components/SideIcons';
import { PLATFORM_ICONS } from '../components/SideIcons/SideIcons';
import ComposeModal from '../components/ComposeModal';
import Footer from '../components/Footer';
import PostMetrics from '../components/PostMetrics/PostMetrics';
import PostTypeIndicator from '../components/PostTypeIndicator/PostTypeIndicator';
import CommentsPanel from '../components/CommentsPanel/CommentsPanel';
import RawResponseModal from '../components/RawResponseModal/RawResponseModal';
import { postsAPI, publishingAPI } from '../api';
import { usePostActions } from '../hooks/usePostActions';
import { getPostPlatforms, hasRawResponse, formatScheduledTime, EDITABLE_STATUSES, PUBLISHABLE_STATUSES } from '../utils/postUtils';
import { canDelete } from '../utils/postDeleteUtils';
import { showToast, showErrorToast } from '../components/Toast/toastUtils';
import './Scheduler.css';

const Scheduler = () => {
  const [currentDate, setCurrentDate] = useState(new Date());
  const [posts, setPosts] = useState([]);
  const [filters, setFilters] = useState({
    status: '',
    postType: ''
  });
  const [activePlatforms, setActivePlatforms] = useState(null); // null = default/allowed, [] = none supported/selected
  const [postsPerPage, setPostsPerPage] = useState(500);
  const [syncingPosts, setSyncingPosts] = useState(false);
  const [showComments, setShowComments] = useState(false);
  const [selectedPostForComments, setSelectedPostForComments] = useState(null);

  const fetchPostsPaginated = async () => {
    const year = currentDate.getFullYear();
    const month = currentDate.getMonth();
    const start = new Date(year, month, 1);
    const end = new Date(year, month + 1, 0, 23, 59, 59);

    const startStr = start.toISOString().split('T')[0];
    const endStr = end.toISOString().split('T')[0];

    try {
      const data = await postsAPI.getPaginated({
        page: 1,
        per_page: postsPerPage,
        sort_by: 'date_range',
        start_date: startStr,
        end_date: endStr
      });
      setPosts(data.posts || data || []);
    } catch (error) {
      console.error('Error fetching posts:', error);
      setPosts([]);
    }
  };

  // Shared post actions hook
  const {
    isComposeModalOpen,
    editingPost,
    editingPostForModal,
    publishingPostId,
    deletingPostId,
    showRawResponse,
    viewingRawResponsePost,
    handleComposeClick,
    handleCloseModal,
    handleSubmitPost,
    openEditForPost,
    handlePublishNow,
    handleDeletePost,
    handleViewRawResponse,
    setShowRawResponse,
  } = usePostActions(fetchPostsPaginated);

  useEffect(() => {
    fetchPostsPaginated();
  }, [currentDate, postsPerPage]);

  const handleDeletePlatformPost = async (post, platform) => {
    const status = post?.platform_statuses?.[platform];
    const statusId = status?.post_platform_status_id;
    const postId = post?.id;
    
    if (!window.confirm(`Delete this ${platform} post from the platform?`)) return;

    try {
      if (postId && statusId) {
        await publishingAPI.deletePlatformPost(postId, statusId);
      }
      await fetchPostsPaginated();
      showToast(`${platform} post deleted successfully`);
    } catch (error) {
      showErrorToast(`Delete failed: ${error.message || error}`);
    }
  };

  const getMonthYear = () => {
    const months = ['JANUARY', 'FEBRUARY', 'MARCH', 'APRIL', 'MAY', 'JUNE', 
                   'JULY', 'AUGUST', 'SEPTEMBER', 'OCTOBER', 'NOVEMBER', 'DECEMBER'];
    return `${months[currentDate.getMonth()]} ${currentDate.getFullYear()}`;
  };

  const getCalendarDays = () => {
    const year = currentDate.getFullYear();
    const month = currentDate.getMonth();
    
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    const daysInMonth = lastDay.getDate();
    const startingDayOfWeek = firstDay.getDay();
    
    const days = [];
    
    // Add empty slots for days before the month starts
    for (let i = 0; i < startingDayOfWeek; i++) {
      days.push(null);
    }
    
    // Add all days in the month
    for (let i = 1; i <= daysInMonth; i++) {
      days.push(i);
    }
    
    // Fill remaining slots to complete the grid
    const remainingSlots = 35 - days.length;
    for (let i = 0; i < remainingSlots; i++) {
      days.push(null);
    }
    
    return days;
  };

  const getPostsForDate = (day) => {
    if (!day) return [];
    
    const year = currentDate.getFullYear();
    const month = currentDate.getMonth();
    const dateStr = `${year}-${String(month + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
    
    return (posts || []).filter(post => {
      const pick = post.scheduled_at || post.occurred_at || post.created_at;
      if (!pick) return false;
      try {
        const d = new Date(pick);
        const y = d.getFullYear();
        const m = d.getMonth();
        const dd = d.getDate();
        const itemDateStr = `${y}-${String(m + 1).padStart(2, '0')}-${String(dd).padStart(2, '0')}`;
        if (itemDateStr !== dateStr) return false;
      } catch {
        return false;
      }

      // Status filter applies to local app posts
      if (filters.status && post.status !== filters.status) return false;

      // Post type filter applies to all posts
      if (filters.postType) {
        const pt = (post.post_type || '').toString().toLowerCase();
        if (pt !== filters.postType) return false;
      }
      return true;
    });
  };

  const handleFilterChange = (filterType, value) => {
    setFilters(prev => ({
      ...prev,
      [filterType]: value
    }));
  };

  const clearAllFilters = () => {
    setFilters({
      status: '',
      postType: ''
    });
  };

  const handlePlatformToggle = (platforms) => {
    setActivePlatforms(platforms);
  };

  const goToPreviousMonth = () => {
    setCurrentDate(new Date(currentDate.getFullYear(), currentDate.getMonth() - 1, 1));
  };

  const goToNextMonth = () => {
    setCurrentDate(new Date(currentDate.getFullYear(), currentDate.getMonth() + 1, 1));
  };

  const goToToday = () => {
    setCurrentDate(new Date());
  };

  const handleSyncPosts = async () => {
    // Sync ALL connected platforms - activePlatforms is UI filter only, irrelevant for sync
    setSyncingPosts(true);
    try {
      const year = currentDate.getFullYear();
      const month = currentDate.getMonth();
      const start = new Date(year, month, 1);
      const end = new Date(year, month + 1, 0, 23, 59, 59);

      const startStr = start.toISOString().split('T')[0];
      const endStr = end.toISOString().split('T')[0];

      const response = await postsAPI.syncPlatformPosts(startStr, endStr, null);
      const createdCount = response.created_count || 0;
      const skippedCount = response.skipped_count || 0;
      const failedCount = response.failed_count || 0;
      
      if (failedCount > 0) {
        showErrorToast(`Sync completed with issues: ${createdCount} created, ${skippedCount} skipped, ${failedCount} failed`);
      } else {
        showToast(`Sync completed: ${createdCount} created, ${skippedCount} skipped`);
      }
      
      // Refresh posts after sync
      await fetchPostsPaginated();
    } catch (error) {
      console.error('Error syncing posts:', error);
      showErrorToast(`Sync failed: ${error.message || error}`);
    } finally {
      setSyncingPosts(false);
    }
  };

  const weekdays = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
  const calendarDays = getCalendarDays();

  return (
    <div className="scheduler-page">
      <TopNav activePage="scheduler" onComposeClick={handleComposeClick} />
      <SideIcons
        onPlatformToggle={handlePlatformToggle}
        activePlatforms={activePlatforms}
        requireCapability="can_fetch_posts"
      />

      <main className="scheduler-content">
        {Array.isArray(activePlatforms) && activePlatforms.length === 0 && (
          <div className="feature-unavailable" role="status" aria-live="polite">
            <div className="feature-unavailable-card">
              <h2>Calendar sync unavailable</h2>
              <p>
                None of your connected accounts support viewing existing posts in the calendar.
              </p>
              <p>Connect a supported account in Settings to enable this view.</p>
            </div>
          </div>
        )}

        {/* MONTH HEADER + CONTROLS */}
        <section className="month-controls">
          <div className="left-controls">
            <button className="small-btn" onClick={goToPreviousMonth}>&lt;</button>
            <button className="small-btn today" onClick={goToToday}>Today</button>
            <button className="small-btn" onClick={goToNextMonth}>&gt;</button>
            <h1>{getMonthYear()}</h1>
          </div>
          <div className="right-controls">
            <button 
              className="sync-btn" 
              onClick={handleSyncPosts} 
              disabled={syncingPosts}
            >
              {syncingPosts ? 'Syncing...' : 'Sync Posts'}
            </button>
          </div>
        </section>

        {/* FILTER BAR */}
        <section className="filter-bar">
          <div className="filter-item">
            <label>Status</label>
            <select 
              value={filters.status} 
              onChange={(e) => handleFilterChange('status', e.target.value)}
            >
              <option value="">All Statuses</option>
              <option value="draft">Draft</option>
              <option value="scheduled">Scheduled</option>
              <option value="published">Published</option>
              <option value="publish_failed">Publish Failed</option>
            </select>
          </div>

          <div className="filter-item">
            <label>Post Type</label>
            <select
              value={filters.postType}
              onChange={(e) => handleFilterChange('postType', e.target.value)}
            >
              <option value="">All Types</option>
              <option value="text">Text</option>
              <option value="image">Image</option>
              <option value="video">Video</option>
            </select>
          </div>

          <div className="filter-item">
            <label>Posts Per Page</label>
            <select
              value={postsPerPage}
              onChange={(e) => setPostsPerPage(Number(e.target.value))}
            >
              <option value={50}>50</option>
              <option value={100}>100</option>
              <option value={200}>200</option>
              <option value={500}>500</option>
            </select>
          </div>

          <button className="clear" onClick={clearAllFilters}>Clear Status</button>
        </section>

        {/* CALENDAR GRID */}
        <section className="calendar">
          {/* WEEKDAY HEADER */}
          {weekdays.map((day) => (
            <div key={day} className="weekday">{day}</div>
          ))}

          {/* CALENDAR DAYS */}
          {calendarDays.map((day, index) => {
            const dayPosts = getPostsForDate(day);
            return (
              <div key={index} className="day">
                {day && <span className="day-num">{day}</span>}
                {dayPosts.length > 0 && (
                  <div className="posts-container">
                    {dayPosts.map(post => {
                      const postPlatforms = getPostPlatforms(post);
                      const status = post.status;
                      const firstRawStatus = hasRawResponse(post) ? Object.values(post.platform_statuses).find(ps => ps?.raw_response) : null;

                      return (
                        <div
                          key={post.id}
                          className={`post-item ${status}`}
                          title={post.title || post.content?.substring(0, 50)}
                          onClick={() => openEditForPost(post)}
                        >
                          {/* Platform icons row */}
                          <div className="post-platform-icons">
                            {postPlatforms.map((p) => (
                              <img
                                key={p}
                                className="post-platform-icon"
                                src={PLATFORM_ICONS[p]?.src || ''}
                                alt={p}
                                title={p}
                              />
                            ))}
                          </div>

                          <div className="post-actions" onClick={(e) => e.stopPropagation()}>
                            {/* EDIT */}
                            {EDITABLE_STATUSES.includes(status) && (
                              <button
                                type="button"
                                className="post-action-btn"
                                aria-label="Edit post"
                                title="Edit"
                                onClick={() => openEditForPost(post)}
                              >
                                <svg width="14" height="14" viewBox="0 0 24 24" fill="none">
                                  <path d="M12 20H21" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
                                  <path d="M16.5 3.5a2.1 2.1 0 0 1 3 3L7 19l-4 1 1-4 12.5-12.5Z" stroke="currentColor" strokeWidth="2" strokeLinejoin="round"/>
                                </svg>
                              </button>
                            )}
                            {/* PUBLISH */}
                            {PUBLISHABLE_STATUSES.includes(status) && (
                              <button
                                type="button"
                                className="post-action-btn"
                                aria-label="Publish post now"
                                title={publishingPostId === post?.id ? 'Publishing...' : 'Publish now'}
                                onClick={() => handlePublishNow(post)}
                                disabled={!post || publishingPostId === post?.id}
                              >
                                <svg width="14" height="14" viewBox="0 0 24 24" fill="none">
                                  <path d="M12 3v12" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
                                  <path d="M7 8l5-5 5 5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                                  <path d="M5 21h14" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
                                </svg>
                              </button>
                            )}
                            {/* DELETE */}
                            {canDelete(post) && (
                              <button
                                type="button"
                                className="post-action-btn"
                                aria-label="Delete post"
                                title="Delete"
                                disabled={deletingPostId === post.id}
                                onClick={() => handleDeletePost(post.id, posts)}
                              >
                                <svg width="14" height="14" viewBox="0 0 24 24" fill="none">
                                  <path d="M3 6h18" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
                                  <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
                                  <path d="M8 6V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
                                </svg>
                              </button>
                            )}
                          </div>
                          <div className="post-time">
                            {formatScheduledTime(post.scheduled_at)}
                          </div>
                          <div className="post-title">
                            {post.title || post.content?.substring(0, 30) + (post.content?.length > 30 ? '...' : '')}
                          </div>
                          <div className="post-status-badge">{status}</div>
                          {/* Post type indicators */}
                          <PostTypeIndicator
                            post={post}
                            platform={postPlatforms[0]}
                          />
                          {/* Post metrics */}
                          {status === 'published' && post.metrics && (
                            <PostMetrics
                              metrics={post.metrics}
                              platform={postPlatforms[0]}
                            />
                          )}
                          {/* Comments */}
                          {status === 'published' && (
                            <button
                              type="button"
                              className="post-action-btn"
                              aria-label="View comments"
                              title="View Comments"
                              onClick={(e) => {
                                e.stopPropagation();
                                setSelectedPostForComments(post);
                                setShowComments(true);
                              }}
                            >
                              <svg width="14" height="14" viewBox="0 0 24 24" fill="none">
                                <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                              </svg>
                            </button>
                          )}
                          {/* Raw response */}
                          {hasRawResponse(post) && (
                            <button
                              type="button"
                              className="post-action-btn"
                              aria-label="View raw response"
                              title="View Raw Response"
                              onClick={() => handleViewRawResponse(post)}
                            >
                              <svg width="14" height="14" viewBox="0 0 24 24" fill="none">
                                <path d="M1 12C1 12 5 4 12 4C19 4 23 12 23 12C23 12 19 20 12 20C5 20 1 12 1 12Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                                <circle cx="12" cy="12" r="3" stroke="currentColor" strokeWidth="2"/>
                              </svg>
                            </button>
                          )}
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            );
          })}
        </section>
      </main>

      <ComposeModal 
        isOpen={isComposeModalOpen}
        onClose={handleCloseModal}
        onSubmit={handleSubmitPost}
        onDelete={() => fetchPostsPaginated()}
        editPost={editingPost}
        canViewPlatforms={!!(editingPostForModal && editingPostForModal.platform_statuses && Object.keys(editingPostForModal.platform_statuses || {}).length > 0)}
      />
      
      <RawResponseModal
        isOpen={showRawResponse}
        onClose={() => setShowRawResponse(false)}
        rawResponse={viewingRawResponsePost?.raw_response}
      />

      <CommentsPanel
        isOpen={showComments}
        onClose={() => {
          setShowComments(false);
          setSelectedPostForComments(null);
        }}
        postId={selectedPostForComments?.id}
        postTitle={selectedPostForComments?.title || selectedPostForComments?.content?.substring(0, 50)}
      />
      
      <Footer />
    </div>
  );
};

export default Scheduler;
